import { useCallback, useEffect, useRef, useState } from 'react';
import type { OrderLine, OrderSummary } from '../shared/orderStatus';
import { ApiCallError, lookupOrders } from './api/client';
import { useOrderStatus } from './hooks/useOrderStatus';
import { Brand } from './components/Brand';
import { OrderLookup } from './components/OrderLookup';
import { OrderResults } from './components/OrderResults';
import { OrderHeader } from './components/OrderHeader';
import { LineTracker } from './components/LineTracker';
import { LoadingSkeleton } from './components/LoadingSkeleton';

const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type View =
  | { kind: 'lookup' }
  | { kind: 'lookup-loading' }
  | { kind: 'results'; orders: OrderSummary[] }
  | { kind: 'status' };

function pairCoreLines(lines: OrderLine[]): { line: OrderLine; coreLine?: OrderLine }[] {
  const coreMap = new Map<number, OrderLine>();
  for (const line of lines) {
    if (line.itemSaleType === 'Core' && line.attachedToLineNo != null) {
      coreMap.set(line.attachedToLineNo, line);
    }
  }
  return lines
    .filter((line) => line.itemSaleType !== 'Core')
    .map((line) => ({ line, coreLine: coreMap.get(line.lineNo) }));
}

function readUrlGuid(): string | null {
  const params = new URLSearchParams(window.location.search);
  const id = params.get('id');
  if (id && GUID_RE.test(id)) return id;
  const pathMatch = window.location.pathname.match(/\/track\/([0-9a-f-]{36})/i);
  if (pathMatch && GUID_RE.test(pathMatch[1])) return pathMatch[1];
  return null;
}

// Embedders (the Dynamics CRM side pane) pass lookup values in the fragment,
// e.g. #email=a@b.com&order=SORD1000020, so they never reach server logs.
function readHashLookup(): { email: string; orderNumber: string } | null {
  const params = new URLSearchParams(window.location.hash.slice(1));
  const email = (params.get('email') ?? '').trim();
  const orderNumber = (params.get('order') ?? '').trim();
  return email && orderNumber ? { email, orderNumber } : null;
}

function clearUrlHash() {
  if (!window.location.hash) return;
  const { pathname, search } = window.location;
  window.history.replaceState(window.history.state, '', pathname + search);
}

function writeUrlForOrder(systemId: string | null, mode: 'push' | 'replace' = 'push') {
  const url = new URL(window.location.href);
  // Collapse the /track/<guid> path form into the canonical ?id= query so
  // subsequent URL writes don't stack onto the path.
  url.pathname = url.pathname.replace(/\/track\/[0-9a-f-]{36}\/?$/i, '/');
  if (systemId) {
    url.searchParams.set('id', systemId);
  } else {
    url.searchParams.delete('id');
  }
  const next = url.pathname + url.search + url.hash;
  if (mode === 'replace') {
    window.history.replaceState(window.history.state, '', next);
  } else {
    window.history.pushState({}, '', next);
  }
}

function App() {
  const status = useOrderStatus();
  const [view, setView] = useState<View>({ kind: 'lookup' });
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [hashLookup] = useState(readHashLookup);
  const hashLookupStarted = useRef(false);

  // Deep-link arrival + browser back/forward handling
  useEffect(() => {
    const guid = readUrlGuid();
    if (guid) {
      // Normalize /track/<guid> → ?id=<guid> without adding a history entry.
      writeUrlForOrder(guid, 'replace');
      setView({ kind: 'status' });
      void status.fetch(guid);
    }

    const handlePopState = () => {
      const next = readUrlGuid();
      if (next) {
        setView({ kind: 'status' });
        void status.fetch(next);
      } else {
        setLookupError(null);
        status.clear();
        setView({ kind: 'lookup' });
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If a deep-link GUID fails, generic error and bounce to lookup.
  useEffect(() => {
    if (view.kind !== 'status') return;
    if (status.error && !status.loading) {
      setLookupError(
        status.errorStatus === 404
          ? "We couldn't find that order. Please look it up by email or order number."
          : "We couldn't load that order. Please try again or look it up below.",
      );
      writeUrlForOrder(null, 'replace');
      setView({ kind: 'lookup' });
      status.clear();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.error, status.loading, view.kind]);

  const handleLookup = useCallback(
    async (
      input: { email?: string; orderNumber?: string },
      urlMode: 'push' | 'replace' = 'push',
    ) => {
      setLookupError(null);
      setView({ kind: 'lookup-loading' });
      try {
        const orders = await lookupOrders(input);
        if (orders.length === 1) {
          const id = orders[0].systemId;
          writeUrlForOrder(id, urlMode);
          setView({ kind: 'status' });
          void status.fetch(id);
        } else {
          writeUrlForOrder(null, urlMode);
          setView({ kind: 'results', orders });
        }
      } catch (err) {
        const message = err instanceof ApiCallError ? err.message : 'Lookup failed.';
        setLookupError(message);
        setView({ kind: 'lookup' });
      }
    },
    [status],
  );

  // Fragment-driven lookup on arrival. A ?id= deep link wins if both are present.
  // The ref guards against StrictMode running this effect twice in dev.
  useEffect(() => {
    clearUrlHash();
    if (!hashLookup || hashLookupStarted.current || readUrlGuid()) return;
    hashLookupStarted.current = true;
    void handleLookup(hashLookup, 'replace');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectOrder = useCallback(
    (systemId: string) => {
      writeUrlForOrder(systemId, 'push');
      setView({ kind: 'status' });
      void status.fetch(systemId);
    },
    [status],
  );

  const handleBackToLookup = useCallback(() => {
    writeUrlForOrder(null, 'push');
    setLookupError(null);
    status.clear();
    setView({ kind: 'lookup' });
  }, [status]);

  // ── Render ─────────────────────────────────────────────────────
  if (view.kind === 'lookup' || view.kind === 'lookup-loading') {
    return (
      <div className="min-h-screen">
        <OrderLookup
          onLookup={handleLookup}
          loading={view.kind === 'lookup-loading'}
          error={lookupError}
          initialEmail={hashLookup?.email}
          initialOrderNumber={hashLookup?.orderNumber}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-lg border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center justify-between">
          <button
            onClick={handleBackToLookup}
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-brand-600 transition-colors cursor-pointer"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            New search
          </button>
          <Brand size="sm" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {view.kind === 'results' && (
          <OrderResults
            orders={view.orders}
            onSelect={handleSelectOrder}
            onBack={handleBackToLookup}
          />
        )}

        {view.kind === 'status' && (
          <>
            {status.loading && <LoadingSkeleton />}

            {status.order && (
              <>
                <div className="flex justify-end">
                  <button
                    onClick={() => void status.refresh()}
                    disabled={status.loading}
                    className="
                      inline-flex items-center gap-1.5 text-xs font-medium
                      text-slate-500 hover:text-brand-600 disabled:opacity-50 cursor-pointer
                    "
                  >
                    <svg
                      className={`w-3.5 h-3.5 ${status.loading ? 'animate-spin' : ''}`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={2}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                      />
                    </svg>
                    Refresh
                  </button>
                </div>

                <OrderHeader order={status.order} />

                {pairCoreLines(status.order.lines ?? []).map(({ line, coreLine }) => (
                  <LineTracker
                    key={line.lineNo}
                    line={line}
                    coreLine={coreLine}
                    coreChargeDeferred={status.order?.coreChargeDeferred}
                  />
                ))}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}

export default App;
